import { useState, useEffect, useRef, useMemo } from "react";
import { Table, Input, Select, DatePicker, Skeleton, Empty, Tooltip } from "antd";
import { SearchOutlined, FilterOutlined, ClearOutlined, CalendarOutlined, UserOutlined } from "@ant-design/icons";
import Pagination from "@mui/material/Pagination";
import { toast } from "react-toastify";
import dayjs from "dayjs";
import EnquiryFollowUp from "./EnquiryFollowUp";
import Swal from "sweetalert2";
import { TeamOutlined } from "@ant-design/icons";
import { canTransferLeads } from "../utils/role";
import {
  useInboxLeads,
  useLeadDetailsFollowUp,
  useFollowUpOptions,
  useInternalUserList,
  useAllocateLeads,
  LEAD_STATUS,
} from "../hooks/useManageEnquiry";

const PAGE_SIZE = 10;

/**
 * A lead's `status` is an `option_value` from the lead_follow_up_status master
 * (0 Follow Up … 7 Converted … 10 Negative), so the LABEL must come from that
 * master — never from a hardcoded map here, which drifts the moment someone
 * renames or adds an option in the DB.
 *
 * Only the colours stay local, keyed by the same option_value.
 */
const FOLLOWUP_STATUS_STYLE = {
  "0":  { bg: "#eff6ff",  color: "#2563eb", border: "#bfdbfe" },
  "1":  { bg: "#fff1f2",  color: "#e11d48", border: "#fecdd3" },
  "2":  { bg: "#f5f5f5",  color: "#6b7280", border: "#d1d5db" },
  "3":  { bg: "#f0fdf4",  color: "#16a34a", border: "#bbf7d0" },
  "4":  { bg: "#fffbeb",  color: "#d97706", border: "#fde68a" },
  "5":  { bg: "#fef2f2",  color: "#dc2626", border: "#fecaca" },
  "6":  { bg: "#f5f3ff",  color: "#7c3aed", border: "#ddd6fe" },
  "7":  { bg: "#ecfdf5",  color: "#059669", border: "#6ee7b7" },
  "8":  { bg: "#fff7ed",  color: "#ea580c", border: "#fed7aa" },
  "9":  { bg: "#f1f5f9",  color: "#475569", border: "#cbd5e1" },
  "10": { bg: "#fef2f2",  color: "#dc2626", border: "#fecaca" },
};

const EMPTY_FILTER = { search: "", start_date: null, end_date: null, stage: "" };

/* Same board as All Leads (manageenquiry.jsx) — only the data differs: this one
   is scoped to the leads assigned to the logged-in user. */
const KANBAN_COLUMNS = [
  { key: "new",       label: "New",       color: "#6366f1", border: "#c7d2fe", bg: "#eef2ff" },
  { key: "contacted", label: "Contacted", color: "#0891b2", border: "#bae6fd", bg: "#e0f2fe" },
  { key: "future",    label: "Future",    color: "#d97706", border: "#fde68a", bg: "#fffbeb" },
  { key: "done",      label: "Done",      color: "#16a34a", border: "#86efac", bg: "#dcfce7" },
  { key: "negative",  label: "Negative",  color: "#dc2626", border: "#fca5a5", bg: "#fee2e2" },
];

const thStyle = {
  fontSize: "12px", fontWeight: 700, color: "#475569",
  textTransform: "uppercase", letterSpacing: "0.6px", whiteSpace: "nowrap",
};

export default function AssignedLeads() {
  const [page, setPage]                     = useState(1);
  const [filter, setFilter]                 = useState({ ...EMPTY_FILTER });
  const [appliedFilter, setAppliedFilter]   = useState({ ...EMPTY_FILTER });
  const [filtersOpen, setFiltersOpen]       = useState(true);
  const [selectedLeadId, setSelectedLeadId] = useState(null);
  const [followUpEnquiry, setFollowUpEnquiry] = useState(null);
  const [dueFilter, setDueFilter] = useState("all");     // "all" | "due"
  const [viewMode, setViewMode]   = useState("kanban");  // "kanban" | "list"

  /* ── Transfer leads (super-admin / admin / manager only) ── */
  const mayTransfer = canTransferLeads();
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [transferTo, setTransferTo]               = useState(null);
  const [selectedIds, setSelectedIds]             = useState([]);
  const { data: internalUsers = [] } = useInternalUserList();
  const transferLeads = useAllocateLeads();
  const debounceRef = useRef(null);

  /* ── search debounce ── */
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setAppliedFilter((p) => ({ ...p, search: filter.search }));
      setPage(1);
    }, 500);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [filter.search]);

  /* ── data ── */
  const { data, isFetching, isError } = useInboxLeads(page, {
    search:      appliedFilter.search,
    start_date:  appliedFilter.start_date || "",
    end_date:    appliedFilter.end_date   || "",
    // Stage is derived from `status` + follow-up date, so it can't be a request
    // param — it is applied below. A bigger page is fetched whenever the whole
    // set is needed (board, or a stage filter), so filtering isn't page-limited.
    limit: viewMode === "kanban" || appliedFilter.stage ? 200 : PAGE_SIZE,
  });

  useEffect(() => { if (isError) toast.error("Failed to load assigned leads"); }, [isError]);

  const allLeads = data?.data  || [];
  const total    = data?.total || 0;

  /* Due filter — the list is now the full assigned pipeline, so give a one-click
     way back to "just today's work". Applied to the loaded page; ask the API for
     a `due_filter` param to make it exact across pages. */
  const isDueOrOverdue = (lead) => {
    const nfd = lead.next_followup_date;
    if (!nfd) return true;                       // no date → needs attention
    const d = dayjs(nfd, "DD-MM-YYYY");
    if (!d.isValid()) return true;
    return !d.isAfter(dayjs().startOf("day"), "day");
  };
  const dueFiltered = dueFilter === "due" ? allLeads.filter(isDueOrOverdue) : allLeads;
  const dueCount = allLeads.filter(isDueOrOverdue).length;

  /* ── Kanban ────────────────────────────────────────────────────────────────
     Classification comes entirely from the lead row. `status` holds a
     lead_follow_up_status option_value and stays NULL until someone logs a
     follow-up, so "has any value" == contacted — no per-lead fetch needed. */
  const classifyLead = (item) => {
    const status = String(item.status ?? "").trim();
    if (status === LEAD_STATUS.CONVERTED) return "done";
    if (status === LEAD_STATUS.NEGATIVE)  return "negative";

    let future = status === LEAD_STATUS.FUTURE;
    const nfd = item.next_followup_date;
    if (nfd) {
      const parsed = dayjs(nfd, "DD-MM-YYYY");
      if (parsed.isValid()) {
        future = parsed.isAfter(dayjs().startOf("day"), "day") || future;
      }
    }
    if (future) return "future";

    return status === "" ? "new" : "contacted";
  };

  // Stage filter uses the same classifier as the board, so the dropdown and the
  // columns can never disagree.
  const leads = appliedFilter.stage
    ? dueFiltered.filter((l) => classifyLead(l) === appliedFilter.stage)
    : dueFiltered;

  const kanbanColumns = useMemo(() => {
    const buckets = Object.fromEntries(KANBAN_COLUMNS.map((c) => [c.key, []]));
    leads.forEach((item) => {
      const key = classifyLead(item);
      if (buckets[key]) buckets[key].push(item);
    });
    return KANBAN_COLUMNS.map((c) => ({ ...c, items: buckets[c.key] }));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leads]);

  /* Pick the new owner first, then tick the leads. Ticking cards on the board is
     fiddly, so choosing a user drops you into List view. */
  const handleTransferTargetSelected = (user) => {
    setTransferTo(user);
    setShowTransferModal(false);
    setSelectedIds([]);
    setViewMode("list");
  };

  const handleConfirmTransfer = () => {
    if (!transferTo) { toast.error("Please select an internal user first."); return; }
    if (selectedIds.length === 0) {
      Swal.fire({ icon: "warning", title: "No leads selected", text: "Tick at least one lead to transfer.", confirmButtonColor: "#0891b2" });
      return;
    }
    const limit = transferTo.lead_limit ?? 0;
    const alreadyHas = transferTo.total_assigned_leads ?? 0;
    if (limit > 0 && alreadyHas + selectedIds.length > limit) {
      const canTake = Math.max(0, limit - alreadyHas);
      Swal.fire({
        icon: "error",
        title: "Lead Limit Exceeded",
        html: `<p><strong>${transferTo.name}</strong> has a lead limit of <strong>${limit}</strong>.</p>
               <p>Currently assigned: <strong>${alreadyHas}</strong> &nbsp;·&nbsp; You're trying to add: <strong>${selectedIds.length}</strong></p>
               <p>Can still accept: <strong style="color:${canTake > 0 ? "#16a34a" : "#dc2626"}">${canTake}</strong> more lead(s).</p>`,
        confirmButtonColor: "#0891b2",
      });
      return;
    }
    Swal.fire({
      title: "Confirm Transfer",
      html: `Transfer <strong>${selectedIds.length}</strong> lead(s) to <strong>${transferTo.name}</strong>?`,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Yes, Transfer",
      confirmButtonColor: "#0891b2",
      reverseButtons: true,
    }).then((r) => {
      if (!r.isConfirmed) return;
      transferLeads.mutate(
        { adminId: transferTo.id, leadIds: selectedIds },
        {
          onSuccess: () => {
            Swal.fire({
              icon: "success",
              title: "Transferred!",
              text: `${selectedIds.length} lead(s) moved to ${transferTo.name}.`,
              confirmButtonColor: "#0891b2",
            });
            setSelectedIds([]);
            setTransferTo(null);
          },
          onError: (err) =>
            Swal.fire("Error", err?.message || "Failed to transfer leads.", "error"),
        },
      );
    });
  };

  /* ── follow-up status labels ── */
  const { data: fuOptsRaw = [] } = useFollowUpOptions();
  const fuOpts = Array.isArray(fuOptsRaw) ? fuOptsRaw : (fuOptsRaw?.data || []);
  const fuLabelMap = Object.fromEntries(fuOpts.map((o) => [String(o.option_value), o.label]));

  /* ── lead detail → open EnquiryFollowUp ── */
  const { data: leadDetails } = useLeadDetailsFollowUp(selectedLeadId);
  useEffect(() => {
    if (!leadDetails) return;
    setFollowUpEnquiry({
      id:             leadDetails.id,
      name:           leadDetails.name,
      email:          leadDetails.email,
      phone:          leadDetails.phone,
      company:        leadDetails.company,
      address:        leadDetails.address,
      message:        leadDetails.message,
      status:
        leadDetails.is_positive === 1 ? "Positive"
        : leadDetails.is_positive === 2 ? "Negative"
        : "Neutral",
      assignToId:      leadDetails.assign_to      || null,
      assignedToName:  leadDetails.assign_to_name || "Unassigned",
      stage:           leadDetails.stage_name,
      followUps:       leadDetails.follow_ups     || [],
    });
  }, [leadDetails]);

  /* ── back-button closes panel ── */
  useEffect(() => {
    if (!followUpEnquiry) return;
    window.history.pushState({ fuOpen: true }, "");
    const onPop = () => setFollowUpEnquiry(null);
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, [followUpEnquiry]);

  /* ── helpers ── */
  const activeFilterCount = [
    appliedFilter.stage,
    appliedFilter.start_date,
    appliedFilter.end_date,
  ].filter(Boolean).length;

  const handleApply = () => { setAppliedFilter({ ...filter }); setPage(1); };
  const handleClear = () => {
    setFilter({ ...EMPTY_FILTER });
    setAppliedFilter({ ...EMPTY_FILTER });
    setPage(1);
  };

  /* ── follow-up due badge ── */
  const getDueBadge = (nfd) => {
    if (!nfd) return { label: "No date", bg: "#f9fafb", color: "#9ca3af", border: "#e5e7eb" };
    const d = dayjs(nfd, "DD-MM-YYYY");
    if (!d.isValid()) return { label: "No date", bg: "#f9fafb", color: "#9ca3af", border: "#e5e7eb" };
    const today = dayjs().startOf("day");
    if (d.isBefore(today)) return { label: "Overdue", bg: "#fef2f2", color: "#dc2626", border: "#fecaca" };
    if (d.isSame(today))   return { label: "Due Today", bg: "#fffbeb", color: "#d97706", border: "#fde68a" };
    return { label: d.format("DD-MM-YYYY"), bg: "#f0fdf4", color: "#16a34a", border: "#bbf7d0" };
  };

  /* ── columns ── */
  const columns = [
    {
      title: <span style={{ ...thStyle }}>#</span>,
      key: "index", width: 50, align: "center",
      render: (_, __, idx) => (
        <span style={{ fontSize: 13, color: "#9ca3af", fontWeight: 600 }}>
          {(page - 1) * PAGE_SIZE + idx + 1}
        </span>
      ),
    },
    {
      title: <span style={thStyle}>Lead Name</span>,
      key: "name",
      render: (row) => (
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div style={{ width: 34, height: 34, borderRadius: "50%", background: "linear-gradient(135deg,#0891b2,#0e7490)", display: "flex", alignItems: "center", justifyContent: "center", color: "white", fontSize: 12, fontWeight: 700, flexShrink: 0 }}>
            {(row.name || "?").split(" ").map((w) => w[0]).join("").slice(0, 2).toUpperCase()}
          </div>
          <div>
            <div
              onClick={() => setSelectedLeadId(row.id)}
              style={{ fontWeight: 700, color: "#0891b2", cursor: "pointer", fontSize: 14 }}
            >
              {row.name || "—"}
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
              {row.company && <span style={{ fontSize: 11, color: "#9ca3af" }}>{row.company}</span>}
              {/* Informational only — a demo is just a scheduled meeting on a
                  normal lead now, handled through the usual follow-up flow. */}
              {row.demo_date && (
                <span
                  title="Demo scheduled for this lead"
                  style={{ background: "#f5f3ff", color: "#7c3aed", border: "1px solid #ddd6fe", borderRadius: 20, padding: "0 7px", fontSize: 10, fontWeight: 700 }}
                >
                  📅 Demo {dayjs(row.demo_date).format("DD-MM-YYYY HH:mm")}
                </span>
              )}
            </div>
          </div>
        </div>
      ),
    },
    {
      title: <span style={thStyle}>Email</span>,
      dataIndex: "email", key: "email",
      render: (v) => <span style={{ fontSize: 13, color: "#374151" }}>{v || "—"}</span>,
    },
    {
      title: <span style={thStyle}>Phone</span>,
      key: "phone",
      // Primary (green dot) + alternate number (blue dot) when the lead has one
      render: (_, row) => (
        <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
          {row.phone ? (
            <span style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 13, color: "#374151", fontWeight: 600 }}>
              <span style={{ width: 6, height: 6, borderRadius: "50%", background: "#16a34a", flexShrink: 0 }} />
              {row.phone}
            </span>
          ) : (
            <span style={{ fontSize: 13, color: "#9ca3af" }}>—</span>
          )}
          {row.additional_phone && (
            <span style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 12, color: "#6b7280", fontWeight: 600 }}>
              <span style={{ width: 6, height: 6, borderRadius: "50%", background: "#2563eb", flexShrink: 0 }} />
              {row.additional_phone}
            </span>
          )}
        </div>
      ),
    },
    {
      // "Lead Status", not "Status" — Demo Bookings shows a different one
      title: <span style={thStyle}>Lead Status</span>,
      key: "lead_status",
      render: (_, row) => {
        // Label from the options master, colour by the same option_value
        const val = String(row?.status ?? "");
        const label = fuLabelMap[val];
        if (!label) {
          return <span style={{ fontSize: 12, color: "#9ca3af" }}>—</span>;
        }
        const s = FOLLOWUP_STATUS_STYLE[val] || FOLLOWUP_STATUS_STYLE["0"];
        return (
          <span style={{ background: s.bg, color: s.color, border: `1px solid ${s.border}`, borderRadius: 20, padding: "3px 10px", fontSize: 12, fontWeight: 600, whiteSpace: "nowrap" }}>
            {label}
          </span>
        );
      },
    },
    {
      title: <span style={thStyle}>Follow-up Status</span>,
      key: "followup_status",
      render: (row) => {
        // The list API sends this as follow_up_status; lead-detail sends enquiry_status
        const raw = row.enquiry_status ?? row.follow_up_status;
        const val  = String(raw ?? "0");
        const label = fuLabelMap[val] || raw || "—";
        const s    = FOLLOWUP_STATUS_STYLE[val] || FOLLOWUP_STATUS_STYLE["0"];
        return (
          <span style={{ background: s.bg, color: s.color, border: `1px solid ${s.border}`, borderRadius: 20, padding: "3px 10px", fontSize: 12, fontWeight: 600 }}>
            {label}
          </span>
        );
      },
    },
    {
      title: <span style={thStyle}>Next Follow-up</span>,
      dataIndex: "next_followup_date", key: "next_followup_date",
      render: (v) => {
        const b = getDueBadge(v);
        return (
          <span style={{ background: b.bg, color: b.color, border: `1px solid ${b.border}`, borderRadius: 20, padding: "3px 10px", fontSize: 12, fontWeight: 600, whiteSpace: "nowrap" }}>
            {b.label}
          </span>
        );
      },
    },
    {
      title: <span style={thStyle}>Assigned To</span>,
      dataIndex: "assign_to_name", key: "assign_to_name",
      render: (v) => <span style={{ fontSize: 13, color: "#374151" }}>{v || "—"}</span>,
    },
    {
      title: <span style={thStyle}>Action</span>,
      key: "action",
      render: (row) => (
        <Tooltip title="View / Follow-up">
          <button
            onClick={() => setSelectedLeadId(row.id)}
            style={{ padding: "5px 14px", borderRadius: 8, border: "1px solid #bae6fd", background: "#f0f9ff", color: "#0891b2", fontSize: 12, fontWeight: 600, cursor: "pointer" }}
          >
            Follow-up
          </button>
        </Tooltip>
      ),
    },
  ];

  return (
    <div className="main-content">
      <div className="page-content">
        <div className="container-fluid">

          {/* ── Header ── */}
          <div style={{ background: "linear-gradient(135deg,#0891b2 0%,#0e7490 100%)", borderRadius: 16, padding: "28px 32px", marginBottom: 24, boxShadow: "0 8px 32px rgba(8,145,178,0.28)", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 16 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
              <div style={{ width: 56, height: 56, background: "rgba(255,255,255,0.18)", borderRadius: 14, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 26 }}>
                <UserOutlined style={{ color: "white" }} />
              </div>
              <div>
                <h4 style={{ color: "white", margin: 0, fontSize: 24, fontWeight: 700 }}>Assigned Leads</h4>
                <p style={{ color: "rgba(255,255,255,0.85)", margin: 0, fontSize: 14 }}>
                  Your assigned leads with pending or overdue follow-ups
                </p>
              </div>
            </div>
            {total > 0 && (
              <span style={{ background: "rgba(255,255,255,0.2)", color: "white", border: "1px solid rgba(255,255,255,0.3)", borderRadius: 20, padding: "5px 16px", fontSize: 14, fontWeight: 700 }}>
                {total} leads
              </span>
            )}
          </div>

          {/* ── Filters ── */}
          <div style={{ background: "white", borderRadius: 14, border: "1px solid #e5e7eb", boxShadow: "0 2px 12px rgba(0,0,0,0.05)", marginBottom: 20, overflow: "hidden" }}>
            <div
              onClick={() => setFiltersOpen((o) => !o)}
              style={{ padding: "16px 24px", display: "flex", justifyContent: "space-between", alignItems: "center", cursor: "pointer", background: "#fafbff", borderBottom: filtersOpen ? "1px solid #e5e7eb" : "none" }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <FilterOutlined style={{ color: "#0891b2", fontSize: 16 }} />
                <span style={{ fontWeight: 700, fontSize: 15, color: "#1f2937" }}>Filters</span>
                {activeFilterCount > 0 && (
                  <span style={{ background: "#0891b2", color: "white", borderRadius: 20, padding: "1px 10px", fontSize: 12, fontWeight: 700 }}>
                    {activeFilterCount} active
                  </span>
                )}
              </div>
              <span style={{ color: "#6b7280", fontSize: 13 }}>{filtersOpen ? "▲ Collapse" : "▼ Expand"}</span>
            </div>

            {filtersOpen && (
              <div style={{ padding: "20px 24px" }}>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 14, marginBottom: 16 }}>
                  <Input
                    prefix={<SearchOutlined style={{ color: "#9ca3af" }} />}
                    placeholder="Search name, email, phone..."
                    value={filter.search}
                    onChange={(e) => setFilter((f) => ({ ...f, search: e.target.value }))}
                    style={{ borderRadius: 9, height: 40 }}
                    allowClear
                  />
                  {/* Stage filter mirrors the Kanban columns — is_positive
                      (Positive/Neutral/Negative) is a different thing and was
                      never the lead's status. */}
                  <Select
                    value={filter.stage || undefined}
                    // Applies straight away like the search box — no "Apply Filters"
                    // click. Only the stage key is merged, so an already applied
                    // date range stays put.
                    onChange={(v) => {
                      const stage = v || "";
                      setFilter((f) => ({ ...f, stage }));
                      setAppliedFilter((p) => ({ ...p, stage }));
                      setPage(1);
                    }}
                    placeholder="All Stages"
                    allowClear
                    style={{ width: "100%", height: 40 }}
                    options={KANBAN_COLUMNS.map((c) => ({ value: c.key, label: c.label }))}
                  />
                  <DatePicker
                    placeholder="Start Date"
                    format="DD/MM/YYYY"
                    value={filter.start_date ? dayjs(filter.start_date, "YYYY-MM-DD") : null}
                    onChange={(_, s) => setFilter((f) => ({ ...f, start_date: s ? dayjs(s, "DD/MM/YYYY").format("YYYY-MM-DD") : null }))}
                    style={{ width: "100%", height: 40, borderRadius: 9 }}
                    suffixIcon={<CalendarOutlined style={{ color: "#9ca3af" }} />}
                  />
                  <DatePicker
                    placeholder="End Date"
                    format="DD/MM/YYYY"
                    value={filter.end_date ? dayjs(filter.end_date, "YYYY-MM-DD") : null}
                    onChange={(_, s) => setFilter((f) => ({ ...f, end_date: s ? dayjs(s, "DD/MM/YYYY").format("YYYY-MM-DD") : null }))}
                    style={{ width: "100%", height: 40, borderRadius: 9 }}
                    suffixIcon={<CalendarOutlined style={{ color: "#9ca3af" }} />}
                  />
                </div>
                <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
                  <button onClick={handleClear} style={{ height: 38, padding: "0 20px", borderRadius: 9, border: "1px solid #e5e7eb", background: "white", color: "#6b7280", fontWeight: 600, fontSize: 13, cursor: "pointer", display: "flex", alignItems: "center", gap: 6 }}>
                    <ClearOutlined /> Clear
                  </button>
                  <button onClick={handleApply} style={{ height: 38, padding: "0 24px", borderRadius: 9, border: "none", background: "linear-gradient(135deg,#0891b2,#0e7490)", color: "white", fontWeight: 700, fontSize: 13, cursor: "pointer", display: "flex", alignItems: "center", gap: 6, boxShadow: "0 2px 8px rgba(8,145,178,0.3)" }}>
                    <FilterOutlined /> Apply Filters
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* ── Table ── */}
          <div style={{ background: "white", borderRadius: 16, boxShadow: "0 2px 16px rgba(0,0,0,0.07)", border: "1px solid #f0f0f0", overflow: "hidden" }}>
            <div style={{ padding: "18px 24px", borderBottom: "2px solid #f5f5f5", display: "flex", alignItems: "center", gap: 12 }}>
              <div style={{ width: 38, height: 38, background: "linear-gradient(135deg,#0891b2,#0e7490)", borderRadius: 10, display: "flex", alignItems: "center", justifyContent: "center", color: "white", fontSize: 17 }}>
                <UserOutlined />
              </div>
              <div>
                <div style={{ fontWeight: 700, fontSize: 16, color: "#1f2937" }}>My Assigned Leads</div>
                <div style={{ fontSize: 12, color: "#6b7280" }}>
                  {total} open lead{total !== 1 ? "s" : ""} — stays here until marked Done or Negative
                </div>
              </div>

              {/* Due filter — the list is the full pipeline; this narrows it to today's work */}
              <div style={{ marginLeft: "auto", display: "flex", gap: 6 }}>
                {[
                  { key: "all", label: "All", count: allLeads.length },
                  { key: "due", label: "Due & Overdue", count: dueCount },
                ].map((chip) => {
                  const active = dueFilter === chip.key;
                  return (
                    <button
                      key={chip.key}
                      onClick={() => setDueFilter(chip.key)}
                      style={{
                        padding: "5px 14px", borderRadius: 20, fontSize: 12, fontWeight: 600,
                        cursor: "pointer",
                        border: `1px solid ${active ? "#0891b2" : "#e5e7eb"}`,
                        background: active ? "#0891b2" : "#fff",
                        color: active ? "#fff" : "#6b7280",
                        display: "inline-flex", alignItems: "center", gap: 6,
                      }}
                    >
                      {chip.label}
                      <span style={{
                        background: active ? "rgba(255,255,255,0.25)" : "#f3f4f6",
                        color: active ? "#fff" : "#9ca3af",
                        borderRadius: 20, padding: "0 7px", fontSize: 11, fontWeight: 700,
                      }}>
                        {chip.count}
                      </span>
                    </button>
                  );
                })}

                {/* View toggle — Kanban is the default, same as the old inbox */}
                <div style={{ display: "flex", border: "1px solid #0891b2", borderRadius: 8, overflow: "hidden", marginLeft: 4 }}>
                  {[
                    { key: "kanban", label: "Kanban" },
                    { key: "list", label: "List" },
                  ].map((v) => (
                    <button
                      key={v.key}
                      onClick={() => setViewMode(v.key)}
                      style={{
                        padding: "5px 14px", border: "none", fontSize: 12, fontWeight: 600, cursor: "pointer",
                        background: viewMode === v.key ? "#0891b2" : "#fff",
                        color: viewMode === v.key ? "#fff" : "#0891b2",
                      }}
                    >
                      {v.label}
                    </button>
                  ))}
                </div>

                {/* Transfer — only super-admin / admin / manager can move a lead
                    from one internal user to another. */}
                {mayTransfer && (
                  <>
                    {transferTo && (
                      <span style={{ display: "inline-flex", alignItems: "center", gap: 7, background: "#ecfeff", border: "1px solid #a5f3fc", color: "#0e7490", borderRadius: 20, padding: "4px 12px", fontSize: 12, fontWeight: 600 }}>
                        <TeamOutlined /> {transferTo.name}
                        <span style={{ background: "#cffafe", borderRadius: 10, padding: "1px 7px", fontSize: 11 }}>
                          {transferTo.total_assigned_leads ?? 0} / {transferTo.lead_limit ?? 0}
                        </span>
                        <button
                          onClick={() => { setTransferTo(null); setSelectedIds([]); }}
                          title="Cancel transfer"
                          style={{ border: "none", background: "none", color: "#0e7490", cursor: "pointer", fontWeight: 700, padding: 0, lineHeight: 1 }}
                        >
                          ✕
                        </button>
                      </span>
                    )}
                    <button
                      onClick={transferTo ? handleConfirmTransfer : () => setShowTransferModal(true)}
                      disabled={transferLeads.isPending}
                      style={{ display: "inline-flex", alignItems: "center", gap: 6, background: "linear-gradient(135deg,#0891b2,#0e7490)", color: "#fff", borderRadius: 8, padding: "6px 16px", fontWeight: 600, fontSize: 12, border: "none", cursor: transferLeads.isPending ? "not-allowed" : "pointer", opacity: transferLeads.isPending ? 0.7 : 1, marginLeft: 4 }}
                    >
                      <TeamOutlined />
                      {transferLeads.isPending
                        ? "Transferring…"
                        : transferTo
                          ? `Confirm Transfer${selectedIds.length ? ` (${selectedIds.length})` : ""}`
                          : "Transfer Leads"}
                    </button>
                  </>
                )}
              </div>
            </div>

            {transferTo && (
              <div style={{ padding: "10px 24px", background: "#ecfeff", borderBottom: "1px solid #a5f3fc", color: "#0e7490", fontSize: 13 }}>
                <strong>Transfer mode:</strong>&nbsp;tick the leads to move to <strong>{transferTo.name}</strong>, then click <strong>Confirm Transfer</strong>.
              </div>
            )}

            {isFetching ? (
              <div style={{ padding: 24 }}>
                {[1, 2, 3, 4, 5].map((i) => (
                  <Skeleton key={i} active paragraph={{ rows: 1 }} style={{ marginBottom: 14 }} />
                ))}
              </div>
            ) : leads.length === 0 ? (
              <div style={{ padding: "60px 24px", textAlign: "center" }}>
                <Empty description={<span style={{ color: "#9ca3af" }}>No assigned leads found</span>} />
              </div>
            ) : viewMode === "kanban" ? (
              /* ═══ KANBAN VIEW ═══ */
              <div style={{ padding: "20px 24px", overflowX: "auto" }}>
                <div style={{ display: "flex", gap: 16, alignItems: "flex-start", minWidth: "fit-content" }}>
                  {kanbanColumns.map((col) => (
                    <div key={col.key} style={{ flex: "0 0 300px", background: "#f8fafc", borderRadius: 14, border: `1px solid ${col.border}`, overflow: "hidden" }}>
                      {/* Column header */}
                      <div style={{ padding: "12px 16px", display: "flex", alignItems: "center", gap: 8, borderBottom: `2px solid ${col.border}`, background: "#fff" }}>
                        <span style={{ background: col.bg, color: col.color, border: `1px solid ${col.border}`, borderRadius: 20, padding: "3px 12px", fontSize: 12, fontWeight: 700 }}>
                          {col.label}
                        </span>
                        <span style={{ background: col.color, color: "#fff", borderRadius: "50%", width: 20, height: 20, display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 700 }}>
                          {col.items.length}
                        </span>
                      </div>

                      {/* Cards */}
                      <div style={{ padding: 12, display: "flex", flexDirection: "column", gap: 10, maxHeight: 620, overflowY: "auto" }}>
                        {col.items.length === 0 ? (
                          <div style={{ textAlign: "center", padding: "32px 16px", color: "#d1d5db", fontSize: 13 }}>No leads</div>
                        ) : col.items.map((item) => {
                          const due = getDueBadge(item.next_followup_date);
                          const st = String(item.status ?? "");
                          const stLabel = fuLabelMap[st];
                          const stStyle = FOLLOWUP_STATUS_STYLE[st] || FOLLOWUP_STATUS_STYLE["0"];
                          return (
                            <div
                              key={item.id}
                              onClick={() => setSelectedLeadId(item.id)}
                              style={{ background: "#fff", borderRadius: 12, padding: "14px 14px 10px", border: "1px solid #f0f0f0", boxShadow: "0 1px 4px rgba(0,0,0,0.05)", cursor: "pointer", transition: "box-shadow 0.15s" }}
                              onMouseEnter={(e) => (e.currentTarget.style.boxShadow = "0 4px 14px rgba(0,0,0,0.1)")}
                              onMouseLeave={(e) => (e.currentTarget.style.boxShadow = "0 1px 4px rgba(0,0,0,0.05)")}
                            >
                              <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
                                <div style={{ width: 36, height: 36, borderRadius: "50%", background: "linear-gradient(135deg,#0891b2,#0e7490)", display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontSize: 12, fontWeight: 700, flexShrink: 0 }}>
                                  {(item.name || "?").split(" ").map((w) => w[0]).join("").slice(0, 2).toUpperCase()}
                                </div>
                                <div style={{ minWidth: 0 }}>
                                  <div style={{ fontWeight: 600, fontSize: 13, color: "#111827", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                    {item.name || "—"}
                                  </div>
                                  {item.company && (
                                    <div style={{ fontSize: 11, color: "#9ca3af", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{item.company}</div>
                                  )}
                                </div>
                              </div>

                              <div style={{ fontSize: 12, color: "#6b7280", display: "flex", flexDirection: "column", gap: 4, marginBottom: 10 }}>
                                {item.email && <div style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>✉ {item.email}</div>}
                                {item.phone && <div style={{ fontWeight: 600, color: "#374151" }}>📞 {item.phone}</div>}
                                {item.additional_phone && (
                                  <div style={{ fontWeight: 600, color: "#6b7280", fontSize: 11 }}>
                                    📱 {item.additional_phone}
                                  </div>
                                )}
                              </div>

                              <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap", paddingTop: 10, borderTop: "1px solid #f3f4f6" }}>
                                <span style={{ background: due.bg, color: due.color, border: `1px solid ${due.border}`, borderRadius: 20, padding: "2px 9px", fontSize: 11, fontWeight: 700 }}>
                                  {due.label}
                                </span>
                                {stLabel && (
                                  <span style={{ background: stStyle.bg, color: stStyle.color, border: `1px solid ${stStyle.border}`, borderRadius: 20, padding: "2px 9px", fontSize: 11, fontWeight: 600 }}>
                                    {stLabel}
                                  </span>
                                )}
                                {item.demo_date && (
                                  <span style={{ background: "#f5f3ff", color: "#7c3aed", border: "1px solid #ddd6fe", borderRadius: 20, padding: "2px 8px", fontSize: 10, fontWeight: 700 }}>
                                    📅 Demo {dayjs(item.demo_date).format("DD-MM-YYYY HH:mm")}
                                  </span>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="table-responsive">
                <Table
                  dataSource={leads}
                  columns={columns}
                  rowKey="id"
                  pagination={false}
                  loading={false}
                  // Checkboxes only once a transfer target has been picked
                  rowSelection={
                    transferTo
                      ? {
                          selectedRowKeys: selectedIds,
                          onChange: (keys) => setSelectedIds(keys),
                        }
                      : undefined
                  }
                  onRow={(row) => ({
                    style: { cursor: "pointer" },
                    onClick: () => {
                      // In transfer mode a row click ticks the box instead of
                      // opening the lead
                      if (transferTo) {
                        setSelectedIds((prev) =>
                          prev.includes(row.id)
                            ? prev.filter((id) => id !== row.id)
                            : [...prev, row.id],
                        );
                        return;
                      }
                      setSelectedLeadId(row.id);
                    },
                    onMouseEnter: (e) => { e.currentTarget.style.background = "#f0f9ff"; },
                    onMouseLeave: (e) => { e.currentTarget.style.background = ""; },
                  })}
                  style={{ borderRadius: 0 }}
                  size="middle"
                />
              </div>
            )}

            {!isFetching && total > PAGE_SIZE && (
              <div style={{ padding: "16px 24px", borderTop: "1px solid #f0f0f0", display: "flex", justifyContent: "center", background: "#fafbff" }}>
                <Pagination
                  count={Math.ceil(total / PAGE_SIZE)}
                  page={page}
                  onChange={(_, v) => setPage(v)}
                  color="primary"
                />
              </div>
            )}
          </div>

        </div>
      </div>

      {/* ── Pick who to transfer the leads to ── */}
      {showTransferModal && (
        <div
          onClick={(e) => { if (e.target === e.currentTarget) setShowTransferModal(false); }}
          style={{ position: "fixed", inset: 0, zIndex: 1200, background: "rgba(15,23,42,.55)", backdropFilter: "blur(5px)", display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}
        >
          <div style={{ background: "#fff", borderRadius: 18, width: "100%", maxWidth: 580, overflow: "hidden", boxShadow: "0 20px 60px rgba(8,145,178,.25)", border: "2px solid #cffafe" }}>
            <div style={{ background: "linear-gradient(135deg,#0891b2,#0e7490)", padding: "16px 22px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <div style={{ width: 34, height: 34, background: "rgba(255,255,255,.2)", borderRadius: 9, display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontSize: 16 }}>
                  <TeamOutlined />
                </div>
                <div>
                  <div style={{ color: "#fff", fontWeight: 700, fontSize: 15 }}>Transfer Leads</div>
                  <div style={{ color: "rgba(255,255,255,.8)", fontSize: 11 }}>Move leads to another internal user</div>
                </div>
              </div>
              <button
                onClick={() => setShowTransferModal(false)}
                style={{ width: 28, height: 28, borderRadius: "50%", background: "rgba(255,255,255,.2)", border: "none", color: "#fff", cursor: "pointer", fontSize: 14 }}
              >
                ✕
              </button>
            </div>

            <div style={{ padding: "20px 22px", maxHeight: "60vh", overflowY: "auto" }}>
              {internalUsers.length === 0 ? (
                <div style={{ textAlign: "center", padding: "40px 0", color: "#9ca3af" }}>
                  <TeamOutlined style={{ fontSize: 40, marginBottom: 10, display: "block" }} />
                  <div>No internal users found</div>
                </div>
              ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                  {internalUsers.map((user) => {
                    const used = user.total_assigned_leads ?? 0;
                    const limit = user.lead_limit ?? 0;
                    const pct = limit > 0 ? Math.min(100, Math.round((used / limit) * 100)) : 0;
                    const overLimit = limit > 0 && used >= limit;
                    return (
                      <div
                        key={user.id}
                        onClick={() => { if (!overLimit) handleTransferTargetSelected(user); }}
                        title={overLimit ? `${user.name} has reached their lead limit (${limit})` : ""}
                        style={{
                          border: `1.5px solid ${overLimit ? "#fecaca" : "#cffafe"}`,
                          background: overLimit ? "#fff5f5" : "#f8feff",
                          borderRadius: 12, padding: "12px 14px",
                          cursor: overLimit ? "not-allowed" : "pointer",
                          opacity: overLimit ? 0.7 : 1,
                        }}
                      >
                        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
                          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                            <div style={{ width: 36, height: 36, borderRadius: "50%", background: "linear-gradient(135deg,#0891b2,#0e7490)", display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontSize: 13, fontWeight: 700, flexShrink: 0 }}>
                              {user.name?.split(" ").map((w) => w.charAt(0)).join("").slice(0, 2).toUpperCase() || "?"}
                            </div>
                            <div>
                              <div style={{ fontWeight: 700, fontSize: 14, color: "#1f2937" }}>{user.name}</div>
                              <div style={{ fontSize: 12, color: "#6b7280" }}>{user.email}</div>
                            </div>
                          </div>
                          <div style={{ textAlign: "right" }}>
                            <span style={{ background: overLimit ? "#fef2f2" : "#ecfeff", color: overLimit ? "#dc2626" : "#0e7490", border: `1px solid ${overLimit ? "#fecaca" : "#a5f3fc"}`, borderRadius: 20, padding: "3px 10px", fontSize: 12, fontWeight: 700 }}>
                              {used} / {limit} leads
                            </span>
                            {overLimit && (
                              <div style={{ fontSize: 11, color: "#dc2626", marginTop: 3, fontWeight: 600 }}>Limit reached</div>
                            )}
                          </div>
                        </div>
                        <div style={{ background: "#f3f4f6", borderRadius: 99, height: 6, overflow: "hidden" }}>
                          <div style={{ height: "100%", borderRadius: 99, width: `${pct}%`, background: pct >= 100 ? "linear-gradient(90deg,#ef4444,#dc2626)" : pct >= 80 ? "linear-gradient(90deg,#f59e0b,#d97706)" : "linear-gradient(90deg,#22d3ee,#0891b2)", transition: "width .4s ease" }} />
                        </div>
                        <div style={{ display: "flex", justifyContent: "space-between", marginTop: 4, fontSize: 11, color: "#9ca3af" }}>
                          <span>Role: <strong>{user.role}</strong></span>
                          <span>{pct}% used &nbsp;·&nbsp; {Math.max(0, limit - used)} remaining</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            <div style={{ padding: "14px 22px", borderTop: "1px solid #f0f0f0", background: "#fafafa", display: "flex", justifyContent: "flex-end" }}>
              <button
                onClick={() => setShowTransferModal(false)}
                style={{ padding: "8px 22px", background: "#f1f5f9", color: "#6b7280", border: "1.5px solid #e2e8f0", borderRadius: 10, fontSize: 13, fontWeight: 600, cursor: "pointer" }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── EnquiryFollowUp panel — full-screen overlay above the app chrome,
             same wrapper All Leads / Inbox / Future use. Without it the panel
             renders inside .main-content and the sidebar clips its left column. */}
      {followUpEnquiry && (
        <div style={{ position: "fixed", inset: 0, zIndex: 1050, background: "#f1f5f9", animation: "slideInRight .28s ease" }}>
          <EnquiryFollowUp
            enquiry={followUpEnquiry}
            onClose={() => { setFollowUpEnquiry(null); setSelectedLeadId(null); }}
          />
        </div>
      )}
    </div>
  );
}
